/**
 * The wording the event-interest surfaces share, in ONE copy.
 *
 * Two of the sentences below are not decoration and not helper text: they are
 * the two things a reader of these figures cannot work out from the figures.
 * They are quoted from the feature contract rather than paraphrased, because a
 * second paraphrase is how two screens come to describe one number differently.
 *
 * ⛔ **NOTHING HERE FETCHES OR COMPUTES.** It decides what a surface may SAY,
 * given what the server said. The demand arithmetic is the server's and a count
 * summed in a browser is the defect this module exists beside — see
 * {@link CITY_DEMAND_COMPOSITION}.
 */

import type { EventInterestFormRegion } from '@/lib/types';

/**
 * 🔴 **THE SENTENCE EVERY CITY FIGURE MUST CARRY, AND IT IS THE DOCUMENTED
 * EXCEPTION TO THE NO-HELPER-TEXT PROHIBITION RATHER THAN A CARVE-OUT FROM
 * IT.**
 *
 * That prohibition forbids prose attached to a CONTROL — a sentence competing
 * with the thing it sits beside, read by people who did not want it. This is a
 * **fact about the DATA that the reader cannot otherwise see**, which is one of
 * the four kinds the rule keeps, alongside errors, refusals and
 * why-a-control-is-disabled. ⚠️ **A helper-text sweep must not delete it**, and
 * the next person running one is right to have stopped here to check.
 *
 * The fact: an `all_cities` respondent stores **no city rows at all**
 * (contract §3), deliberately — materialising "all" goes stale the moment a
 * stop changes. So demand for a city is the institutions that NAMED it plus
 * every whole-tour taker on a festival that visits it.
 *
 * ⛔ **The wrong conclusion it prevents is the whole reason it is here: a
 * figure built from the named picks alone is LOW, and it looks entirely
 * plausible.** There is no error, no blank cell and no marker — a smaller
 * number reads as less interest. The contract calls this the single most
 * dangerous thing in the schema for whoever writes a report, and says to say so
 * at every query that counts.
 */
export const CITY_DEMAND_COMPOSITION = {
  short: 'Every city figure is explicit picks plus whole-tour takers.',
  detail:
    'An institution that asks for the whole tour names no city of its own, so it is counted on every city that festival visits. A figure built from the named picks alone is lower than the real one, and it looks entirely plausible.',
} as const;

/**
 * 🔴 **THE SECOND SENTENCE EVERY DEMAND FIGURE MUST CARRY**, and the same
 * exception applies for the same reason: nothing on screen distinguishes an
 * institution somebody has approved from one nobody has looked at.
 *
 * Contract ruling 3: a submission attaches to its company **immediately**, even
 * while that company is unapproved. So these counts include institutions nobody
 * has approved yet — which is CORRECT, because they did express interest — but
 * it has to be visible (contract §8).
 *
 * ⛔ **A single blended number is the one somebody quotes in a meeting**, which
 * is why the split is a pair of columns rather than a footnote on a total.
 */
export const UNAPPROVED_SPLIT_NOTE = {
  /*
   * ⚠️ **NO "YET" IN EITHER SENTENCE, for the reason `DEMAND_LABELS.notApproved`
   * spells out**: a rejected institution's submissions survive and are counted
   * here too, and "yet" promises that institution an approval it will never
   * get. The figure is neutral about which, and the copy has to be as well.
   */
  short: 'Institutions nobody has approved are counted separately.',
  detail:
    'Interest is recorded when the form is submitted, which is before anybody approves the institution — and a rejected one is kept too. Both are real interest and both are counted, in their own column, so the figure can be read either way round.',
} as const;

/**
 * ⛔ **NO INSTITUTION COUNT IS EVER SUMMED IN THE BROWSER, AND THIS IS WHERE
 * THAT IS WRITTEN DOWN.**
 *
 * One institution wanting three cities is three rows, so adding a column of
 * city figures counts it three times. The arithmetic looks like a total and is
 * not one — it is a count of ROW MEMBERSHIPS — and nothing about it errors.
 * ⚠️ **A festival-level institution count must come from the server**; where
 * the server has not sent one, a surface shows the cities and says nothing
 * about the whole.
 */
export const NO_CLIENT_SIDE_DEMAND_TOTAL =
  'Counts are per city. One institution can appear under several cities, so these do not add up to a number of institutions.';

/* -------------------------------------------------------------------------- *
 * Column labels
 * -------------------------------------------------------------------------- */

/**
 * The demand columns, named so the composition is legible from the headings
 * alone and the sentence above is a confirmation rather than the only clue.
 *
 * ⚠️ **"Named it" and "Whole tour" decompose the total; "Not approved" is a
 * SUBSET of it.** Those are two different relationships and the headings are
 * worded so — the pair adds up, the subset does not, and a reader adding the
 * subset back on reports more interest than exists.
 *
 * ⛔ **THERE IS NO "Approved" COLUMN, AND ITS ABSENCE IS DELIBERATE.** The wire
 * carries `unapprovedCompanies` and not its complement, because §8 requires the
 * UNAPPROVED half to be visible while the headline stays inclusive. A derived
 * `total − unapproved` column would be the one figure on this report that no
 * server ever stated, sitting between two that were — and the split is already
 * legible without it.
 */
export const DEMAND_LABELS = {
  city: 'City',
  festival: 'Festival',
  /**
   * ⚠️ **"Institutions", never "Interest" or "Demand".** The figure counts
   * institutions, and a heading naming the abstraction lets a reader take it
   * for a score.
   */
  institutions: 'Institutions',
  explicit: 'Named it',
  wholeTour: 'Whole tour',
  /**
   * 🔴 **"Not approved", NOT "Awaiting approval", and the difference is the
   * same three-state trap `pursuitsCreatedAt` carries.**
   *
   * The figure counts institutions nobody has approved. **Whether a given one
   * is PENDING or REJECTED is not in it** — a rejected company's submissions
   * survive deliberately (contract ruling 4) and are counted here like any
   * other. So "Awaiting approval" promises an approval that some of these
   * institutions will never get, which is a claim the number cannot support.
   *
   * ⚠️ **"Not approved" is neutral about which**, and that is the honest shape:
   * the demand report says how many are outside the approved set, and the
   * submissions list is where a reader asks which of the two each one is.
   */
  notApproved: 'Not approved',
} as const;

/* -------------------------------------------------------------------------- *
 * The answers, as a reader reads them
 * -------------------------------------------------------------------------- */

/**
 * The form region a submission was answered under.
 *
 * ⚠️ **IT TRAVELS WITH THE SUBMISSION so that "did not answer" and "was never
 * asked" do not look the same** (contract §2). A festival absent from a
 * submission means one or the other, and only the region says which.
 *
 * 🔴 **`north-america` IS LOWERCASE WITH A HYPHEN, AND A THIRD LOOKALIKE LIVES
 * IN THIS CODEBASE ONE CHARACTER AWAY.** `BillingRegion` carries
 * **`north_america`, with an UNDERSCORE**, also meaning North America, from
 * migration 138. **Neither is wrong and neither can move**: the billing one is
 * a MySQL ENUM member whose INDEX is stored, and this one is `admin-app`'s wire
 * format, already in their tables.
 *
 * ⛔ **A FIND-AND-REPLACE NORMALISING EITHER SILENTLY BREAKS THE OTHER**, and
 * nothing matches across the boundary, so no type can catch it. Spelled with an
 * underscore this is a **422 naming `formRegion` on every submission from the
 * North America form** — on a `.strict()` schema, for a value that is correct
 * at their end and correct-LOOKING at ours.
 *
 * ⛔ **AND THE EVENT KEYS USE UNDERSCORES WHILE THIS USES A HYPHEN**
 * (`india_fall_2027`). They are inconsistent with each other and both are
 * correct; harmonising either towards the other breaks it.
 *
 * ── WHY THIS IS A UNION-KEYED MAP AND NOT AN `if` CHAIN ─────────────────────
 *
 * ⛔ **A `Record<string, string>` — or a chain taking a bare `string` — DEFEATS
 * THE TYPE ENTIRELY, and it has already bitten once here.** It accepts the
 * stale spelling, typechecks, and renders the raw value: `north-america form`
 * where *North America form* belongs. Keyed on the union instead, a stale
 * spelling at a call site is a **compile error**, and a third region added to
 * the union is a compile error here too — which is the reminder to word it
 * rather than let it fall through as a slug.
 */
const FORM_REGION_LABELS: Record<EventInterestFormRegion, string> = {
  'north-america': 'North America',
  global: 'Global',
};

export function formRegionLabel(region: EventInterestFormRegion): string {
  /*
   * ⚠️ **The annotation is what makes the fallback legitimate rather than dead
   * code.** Indexing is exhaustive over the union, so this cannot miss for a
   * value this bundle knows — the `??` is for a SERVER AHEAD OF THIS CLIENT,
   * which is reachable because `form_region` is a `VARCHAR` validated in zod
   * rather than a SQL `ENUM`: a third variant is a schema edit, and it can land
   * before this union does. Rendering the raw value keeps its arrival VISIBLE;
   * a blank cell would hide exactly the thing worth noticing.
   */
  const label: string | undefined = FORM_REGION_LABELS[region];
  return label ?? region;
}

/**
 * The two form regions as pickable options.
 *
 * ⛔ **`value` IS TYPED TO THE UNION, so a stale spelling in a filter's option
 * list is a compile error** rather than a `?formRegion=north_america` that 422s
 * and empties the table — which reads as *"no institution used that form"*.
 * `ComboboxOption.value` is a bare `string`, so a literal written at a call
 * site is checked by nothing; this list is where that check lives.
 */
export const FORM_REGION_OPTIONS: readonly { value: EventInterestFormRegion; label: string }[] = [
  { value: 'north-america', label: FORM_REGION_LABELS['north-america'] },
  { value: 'global', label: FORM_REGION_LABELS.global },
];

/**
 * The same label, for a region that arrives as an **untyped `string`**.
 *
 * ⚠️ **THIS IS FOR RUNTIME DATA ONLY — a query-string value, or a column
 * funnel's option key, where the type system genuinely has nothing to go on.**
 * `ColumnSpec.format` is handed a `string` because the hook cannot know a
 * column's vocabulary.
 *
 * ⛔ **IT IS NOT THE ONE TO REACH FOR AT A CALL SITE THAT HOLDS A TYPED
 * VALUE**, and the split is the whole defence the contract asks for: keeping
 * {@link formRegionLabel} narrow to the union is what makes a stale spelling a
 * compile error, and widening it to `string` to save this second export would
 * give that up everywhere to serve one caller.
 *
 * ⚠️ **An unknown value renders as ITSELF**, which is the honest answer for
 * data this bundle has not been told about — and it is the visible failure
 * rather than the blank one.
 */
export function formRegionLabelOf(raw: string): string {
  const label: string | undefined = (FORM_REGION_LABELS as Record<string, string>)[raw];
  return label ?? raw;
}

/**
 * Whether a festival was said yes or no to.
 *
 * ⚠️ **A row exists for `no` as well as for `yes`** (contract §3), and that is
 * the point: "said no" and "was never asked" are different facts, and the
 * second is the ABSENCE of a row. So a `no` is rendered as an answer rather
 * than dropped — dropping it would make a considered refusal indistinguishable
 * from a festival the form never put in front of them.
 */
export function responseLabel(response: string): string {
  if (response === 'yes') return 'Yes';
  if (response === 'no') return 'No';
  return response;
}

/**
 * What a `yes` included, or why there is nothing to show.
 *
 * ⚠️ **`null` IS NOT "PARTICIPATION ONLY".** It means the question was not
 * asked — `inclusion` is required when the answer is `yes` and refused when it
 * is `no` (contract §5), so a null beside a `no` is the schema working. ⛔ A
 * `?? 'Participation only'` would print a purchase nobody made.
 */
export function inclusionLabel(inclusion: string | null): string | null {
  if (inclusion === null) return null;
  if (inclusion === 'travel_package') return 'Travel package';
  if (inclusion === 'participation_only') return 'Participation only';
  return inclusion;
}

/**
 * 🔴 **THE ONE WORDING FOR AN `all_cities` ANSWER, ACROSS EVERY SURFACE THAT
 * RENDERS ONE.** The contract states it for the company panel in exactly these
 * words (§8), and this constant is where the tracker takes them from so the two
 * screens cannot say it differently.
 *
 * ⛔ **IT IS THE ANSWER THEY GAVE, AND IT IS NEVER MATERIALISED FROM THE
 * PROJECT.** Listing the project's current stops instead would go on claiming a
 * stop that has since been dropped and silently omit one that has been added —
 * which is the whole reason an `all_cities` answer stores no city rows.
 *
 * ⚠️ **If a second surface ever words this differently, a reader takes the
 * difference for a difference in the data.** Import it; do not retype it.
 */
export const ALL_CITIES_LABEL = 'All cities on this tour';

/**
 * Which cities a `yes` asked for.
 *
 * 🔴 **`all_cities` IS THE ANSWER THAT STORES NOTHING**, so it must be worded
 * from the SELECTION and never from the city list: a row with no cities is
 * either the whole tour or a single-location festival, and an empty list alone
 * cannot tell them apart. ⛔ Rendering "no cities" for a whole-tour taker
 * reverses their answer.
 *
 * ⚠️ **`null` means the festival tours one place and they were asked neither**
 * — three of the seven (contract §5) — which is again not "no cities".
 */
export function selectionLabel(selection: string | null): string | null {
  if (selection === null) return null;
  if (selection === 'all_cities') return ALL_CITIES_LABEL;
  if (selection === 'select_cities') return 'Selected cities';
  return selection;
}

/**
 * The sentence for a festival answered `yes` that names no city.
 *
 * ⚠️ **Both branches are ordinary states and neither is a gap**, which is why
 * neither is an em dash: a single-location festival has no cities to name, and
 * a whole-tour taker deliberately named none.
 */
export function noCitiesReason(selection: string | null): string {
  if (selection === 'all_cities') return ALL_CITIES_LABEL;
  return 'This festival runs in one place';
}
